import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Ship, ShieldCheck, KeyRound, User } from "lucide-react";

const DEMO = [
  { label: "Admin / Owner", id: "ADM001", role: "admin" },
  { label: "Staff Gudang", id: "GDG001", role: "staff_gudang" },
  { label: "Staff Sales", id: "SLS001", role: "staff_sales" },
];

export default function Login() {
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    if (!employeeId || !password) { toast.error("ID Karyawan & Password wajib diisi"); return; }
    setLoading(true);
    try {
      await login(employeeId.trim(), password);
      toast.success("Selamat datang kembali");
      nav("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Gagal masuk";
      toast.error(typeof msg === "string" ? msg : "Gagal masuk");
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (id) => { setEmployeeId(id); setPassword("password123"); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950">
      {/* Left hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1605745341112-85968b19335b?crop=entropy&cs=srgb&fm=jpg&q=85')" }} />
        <div className="absolute inset-0 auth-hero-overlay" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-2xl">
            <span className="font-display font-extrabold text-navy-900 text-xl">N</span>
          </div>
          <div>
            <div className="font-display font-bold text-white text-xl">FUJI INDO SURYA</div>
            <div className="text-xs uppercase tracking-widest text-yellow-500/80">Perdagangan Umum</div>
          </div>
        </div>
        <div className="relative z-10 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur">
            <Ship size={14} className="text-yellow-400" />
            <span className="text-xs text-white/90 font-medium">Sistem Terintegrasi B2B</span>
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-extrabold text-white leading-tight tracking-tight">
            Kelola bisnis dagang Anda dengan <span className="text-yellow-400">presisi korporat</span>.
          </h1>
          <p className="mt-4 text-white/70 leading-relaxed">
            Manajemen inventory, purchasing, invoice otomatis dengan PPN 12%, dan laporan keuangan real-time —
            terpadu dalam satu platform aman berbasis Role-Based Access Control.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 text-white/80">
            {[
              { k: "3", v: "Role Access" },
              { k: "12%", v: "PPN Otomatis" },
              { k: "24/7", v: "Real-time" },
            ].map((s) => (
              <div key={s.v} className="rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur">
                <div className="font-display text-2xl font-bold text-yellow-400">{s.k}</div>
                <div className="text-[11px] uppercase tracking-widest text-white/60">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-white/60 text-xs">
          <ShieldCheck size={14} className="text-yellow-400" />
          <span>Terlindungi enkripsi JWT & bcrypt</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
              <span className="font-display font-extrabold text-navy-900">N</span>
            </div>
            <div>
              <div className="font-display font-bold text-slate-900">FUJI INDO SURYA ERP</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">General Trading</div>
            </div>
          </div>
          <div className="mb-8">
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Portal Karyawan</div>
            <h2 className="mt-2 font-display text-3xl font-extrabold text-slate-900 tracking-tight">Masuk ke Sistem</h2>
            <p className="mt-2 text-sm text-slate-600">Gunakan ID Karyawan yang terdaftar untuk mengakses ERP.</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-slate-700">ID Karyawan</Label>
              <div className="relative mt-1.5">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="login-employee-id-input"
                  className="pl-10 h-11 font-mono"
                  placeholder="Cth. ADM001"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                  autoFocus
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="login-password-input"
                  className="pl-10 h-11"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-navy-900 hover:bg-navy-800 text-white font-display font-semibold tracking-tight"
            >
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500 mb-3">Login cepat (demo)</div>
            <div className="grid grid-cols-3 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => quickFill(d.id)}
                  data-testid={`demo-login-${d.role}`}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left hover:border-yellow-400 hover:bg-yellow-50 transition"
                >
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{d.label}</div>
                  <div className="font-mono text-sm font-semibold text-navy-900 mt-0.5">{d.id}</div>
                </button>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-slate-500">Password default: <span className="font-mono">password123</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
